/**
 * Scheda di un big bag, risolta dal token del QR stampato sull'etichetta.
 *
 * È la pagina che le etichette già stampate puntano da sempre
 * (`FRONTEND_URL/big-bags/{qr_token}`, vedi `_build_etichetta` lato backend):
 * fino a V71 l'URL esisteva e non portava da nessuna parte.
 *
 * Sola lettura, e deve restarlo: chi scansiona in piazzale sta verificando un
 * sacco, non modificandolo. La domanda vera è una — «questo sacco è ancora
 * mio?» — e la risposta è lo stato: in area, oppure uscito con quale FIR e
 * quando.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PackageOpen, ChevronRight } from 'lucide-react';
import { getBigBagByToken, type BigBagByToken } from '../api/uscite';
import { fmtKg } from '../lib/saldo';

function fmtData(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('it-IT') : '—';
}

function Riga({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-stone-mid/60 last:border-0">
      <dt className="text-ink-soft text-sm">{label}</dt>
      <dd className="text-ink-dark text-sm text-right">{children}</dd>
    </div>
  );
}

export default function SchedaBigBagPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { qrToken = '' } = useParams();
  const [bag, setBag] = useState<BigBagByToken | null>(null);
  const [nonTrovato, setNonTrovato] = useState(false);

  useEffect(() => {
    void (async () => {
      try { setBag(await getBigBagByToken(qrToken)); }
      catch { setNonTrovato(true); }
    })();
  }, [qrToken]);

  if (nonTrovato) {
    return (
      <div className="p-6 max-w-xl mx-auto">
        <p className="text-sm text-ink-soft" data-testid="bigbag-non-trovato">
          {t('uscite.scheda_bigbag.non_trovato')}
        </p>
      </div>
    );
  }
  if (!bag) return <div className="p-6 text-sm text-ink-soft">{t('uscite.caricamento')}</div>;

  const uscito = !!bag.uscita_codice;

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <PackageOpen size={20} className="text-sage" />
        <h1 className="text-xl font-semibold text-ink-dark">{t('uscite.scheda_bigbag.titolo')}</h1>
        <span className="font-mono text-xs text-ink-faint">{bag.qr_token.slice(0, 8)}</span>
      </div>

      {/* Lo stato in cima: è la ragione per cui si scansiona. */}
      <div
        data-testid={uscito ? 'bigbag-stato-uscito' : 'bigbag-stato-in-area'}
        className={`rounded-xl border px-4 py-3 mb-5 ${uscito
          ? 'bg-accent-warning-bg border-[#FDE68A] text-ink-dark'
          : 'bg-sage-light border-sage/30 text-sage-dark'}`}
      >
        <p className="text-sm font-medium">
          {uscito
            ? t('uscite.scheda_bigbag.stato_uscito', {
              codice: bag.uscita_codice, data: fmtData(bag.uscita_data_partenza),
            })
            : t('uscite.scheda_bigbag.stato_in_area')}
        </p>
        {uscito && bag.uscita_id && (
          <button type="button" onClick={() => navigate(`/uscite/${bag.uscita_id}`)}
            data-testid="bigbag-link-uscita"
            className="mt-1 text-xs text-sage-dark hover:underline inline-flex items-center gap-1">
            {t('uscite.scheda_bigbag.vedi_uscita')} <ChevronRight size={13} />
          </button>
        )}
      </div>

      <div className="bg-white border border-stone-mid rounded-xl p-4">
        <dl>
          <Riga label={t('uscite.scheda_bigbag.lbl_materiale')}>
            {bag.materiale_nome ?? '—'} <span className="text-ink-faint">{bag.materiale}</span>
          </Riga>
          <Riga label={t('uscite.scheda_bigbag.lbl_peso')}>{fmtKg(bag.peso_kg)} kg</Riga>
          <Riga label={t('uscite.scheda_bigbag.lbl_data_produzione')}>{fmtData(bag.data_produzione)}</Riga>
          <Riga label={t('uscite.scheda_bigbag.lbl_area')}>{bag.area_codice ?? '—'}</Riga>
          <Riga label={t('uscite.scheda_bigbag.lbl_produzione')}>
            <button type="button" onClick={() => navigate('/produzioni')} data-testid="bigbag-link-produzione"
              className="text-sage-dark hover:underline inline-flex items-center gap-1">
              {t('uscite.scheda_bigbag.vedi_produzione')} <ChevronRight size={13} />
            </button>
            <span className="block text-xs text-ink-faint">
              {new Date(bag.produzione_created_at).toLocaleDateString('it-IT')}
            </span>
          </Riga>
        </dl>
      </div>
    </div>
  );
}
